import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { useAuthUser } from '@/hooks';
import { extractLanguageInfo } from '@/utils/languages';

/**
 * Custom hook to handle user redirection based on user type and current path
 * Prevents redirecting if user is already on a specific campaign or collaboration page
 */
export function useUserRedirect() {
  const router = useRouter();
  const user = useAuthUser();

  useEffect(() => {
    if (user) {
      // Only redirect if we're not already on a valid connect route
      // Handle both regular routes and language-prefixed routes (e.g., /en/connect/settings)
      const isOnValidConnectRoute = router.asPath.match(
        /^(?:\/[a-z]{2})?\/connect\/(campaigns|collaborations|settings|team|content-hub|payouts|sentiment)/
      );
      const isOnSpecificCampaignPage = router.asPath.match(
        /^(?:\/[a-z]{2})?\/connect\/campaigns\/[^/]+/
      );
      const isOnSpecificCollaborationPage = router.asPath.match(
        /^(?:\/[a-z]{2})?\/connect\/collaborations\/[^/]+/
      );

      // Also check for campaign sub-routes with language prefixes
      const isOnCampaignSubRoute = router.asPath.match(
        /^(?:\/[a-z]{2})?\/connect\/campaigns\/[^/]+\/(curation|collaborations|drafts|summary|sentiment|beat)/
      );

      if (
        !isOnValidConnectRoute &&
        !isOnSpecificCampaignPage &&
        !isOnSpecificCollaborationPage &&
        !isOnCampaignSubRoute
      ) {
        // Extract language prefix if present (e.g., /en/connect/settings -> /en)
        const { languagePrefix } = extractLanguageInfo(router.asPath);

        if (user.is_client) {
          router.replace(`${languagePrefix}/connect/campaigns`);
        } else {
          router.replace(`${languagePrefix}/connect/collaborations`);
        }
      }
    }
  }, [user, router]);
}
